import { useCallback, useEffect, useState } from "react";
import { Navigate, Link } from "react-router-dom";
import { Show, UserButton, useAuth } from "@clerk/react";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { api, errorText } from "@/lib/api";
import { BookingRow } from "@/components/owner/BookingRow";
import { AccessPanel } from "@/components/owner/AccessPanel";

const FILTERS = [["all", "All"], ["one_on_one", "One-on-one"], ["group", "Group"], ["donation", "Donations"]];

const Stat = ({ label, value, testId }) => (
  <div className="glass rounded-2xl p-5">
    <p className="field-label">{label}</p>
    <p className="font-display gold-text text-3xl mt-2" data-testid={testId}>{value}</p>
  </div>
);

const Dashboard = () => {
  const { getToken } = useAuth();
  const [me, setMe] = useState(null);
  const [data, setData] = useState(null);
  const [filter, setFilter] = useState("all");
  const [tab, setTab] = useState("activity");

  const auth = useCallback(async () => ({ headers: { Authorization: `Bearer ${await getToken()}` } }), [getToken]);

  const load = useCallback(async () => {
    try {
      const cfg = await auth();
      const { data: m } = await api.get("/me", cfg);
      setMe(m);
      if (m.role === "admin") setData((await api.get("/admin/bookings", cfg)).data);
    } catch (e) {
      toast.error(errorText(e));
    }
  }, [auth]);

  useEffect(() => { load(); }, [load]);

  const setStatus = async (id, status) => {
    try {
      await api.patch(`/admin/bookings/${id}/status`, { status }, await auth());
      setData((d) => ({ ...d, items: d.items.map((b) => (b.id === id ? { ...b, status } : b)) }));
      toast.success("Status updated");
    } catch (e) {
      toast.error(errorText(e));
    }
  };

  if (!me) return <div className="flex justify-center py-32"><Loader2 className="animate-spin text-gold" /></div>;
  if (me.role !== "admin") {
    return (
      <div className="glass rounded-[2rem] p-10 max-w-xl mx-auto text-center mt-16" data-testid="owner-not-allowed">
        <h2 className="font-display gold-text text-3xl">Owner only</h2>
        <p className="mt-4 text-[#f7f1e1]/80">This page is only for Queen Boyce. You are signed in as {me.email}.</p>
      </div>
    );
  }
  if (!data) return <div className="flex justify-center py-32"><Loader2 className="animate-spin text-gold" /></div>;

  const items = data.items.filter((b) => filter === "all" || b.kind === filter);
  return (
    <div data-testid="owner-dashboard">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Stat label="Session requests" value={data.summary.one_on_one} testId="stat-one-on-one" />
        <Stat label="Group sign-ups" value={data.summary.group} testId="stat-group" />
        <Stat label="Donations" value={data.summary.donations_paid} testId="stat-donations" />
        <Stat label="Donations received" value={`$${data.summary.total_received.toFixed(2)}`} testId="stat-total" />
      </div>
      <div className="flex flex-wrap gap-3 mt-10">
        {[["activity", "Activity"], ["access", "Access"]].map(([k, l]) => (
          <button key={k} className="choice font-cinzel text-sm" data-active={tab === k} onClick={() => setTab(k)} data-testid={`owner-tab-${k}`}>{l}</button>
        ))}
      </div>
      {tab === "access" ? <AccessPanel auth={auth} /> : (
        <>
          <div className="flex flex-wrap gap-2 mt-6">
            {FILTERS.map(([k, l]) => (
              <button key={k} className="choice !py-2 text-sm" data-active={filter === k} onClick={() => setFilter(k)} data-testid={`owner-filter-${k}`}>{l}</button>
            ))}
            <button className="btn-ghost !py-2 !px-5 !text-xs ml-auto" onClick={load} data-testid="owner-refresh-btn">Refresh</button>
          </div>
          <div className="space-y-4 mt-6" data-testid="owner-bookings-list">
            {items.length === 0 && <p className="text-[#f7f1e1]/60 py-10" data-testid="owner-empty">Nothing here yet.</p>}
            {items.map((b) => <BookingRow key={b.id} b={b} onStatus={setStatus} />)}
          </div>
        </>
      )}
    </div>
  );
};

export default function Owner() {
  return (
    <main className="px-6 md:px-16 py-10 max-w-6xl mx-auto" data-testid="owner-page">
      <header className="flex items-center justify-between mb-10">
        <div>
          <Link to="/" className="font-display gold-text text-3xl">Earth Angel</Link>
          <p className="eyebrow mt-1">Owner sanctuary</p>
        </div>
        <Show when="signed-in"><UserButton /></Show>
      </header>
      <Show when="signed-in"><Dashboard /></Show>
      <Show when="signed-out"><Navigate to="/sign-in" replace /></Show>
    </main>
  );
}
